import {RestService} from "@/services/rest/RestService";
import {
    ContentPageAbstractEndpointsApi,
    ContentPageEndpointsApi,
    ContentPageEnumEndpointsApi
} from "@/api/pharma-cms-content-page";
import {PublicationStatusTypeEnum} from "@/api/enums/publication-status-type-enum";
import {
    CodeDescriptionRestDto,
    ContentPageCreateRestDto,
    ContentPageRestDto,
    IdResponseRestDto,
    IdRestDto,
    PagedContentPageOverviewRestDto, TargetPageInfoRestDto,
    TextWithImageAbstractRestDto,
    WysiwygContentPageRestDto
} from "@/api/pharma-cms-content-page/models";
import {MoveActionEnum} from "@/models/enum/MoveActionEnum";
import {AxiosResponse} from "axios";
import {CustomerContext} from "@/context/CustomerContext";

export class ContentPageRestService extends RestService {

    private apiService: ContentPageEndpointsApi;
    private enumApiService: ContentPageEnumEndpointsApi;
    private abstractApiService: ContentPageAbstractEndpointsApi;

    protected constructor() {
        super();

        this.apiService = new ContentPageEndpointsApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cmsUrl);
        this.enumApiService = new ContentPageEnumEndpointsApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cmsUrl);
        this.abstractApiService = new ContentPageAbstractEndpointsApi(this.getDpAxiosV2Instance(), undefined, this.getConfig().cmsUrl);
    }

    public async updateIsPublishedOnHome(isPublishedOnHome: boolean, pageId: string): Promise<void> {
        await this.apiService.updateIsPublishedOnHome({value: isPublishedOnHome}, pageId);
    }

    public async updatePageStatusCode(status: PublicationStatusTypeEnum, pageId: string): Promise<void> {
        await this.apiService.updatePageStatusCode({value: status}, pageId);
    }

    public async copyContentPage(pageId: string): Promise<void> {
        await this.apiService.copyContentPage(pageId);
    }

    public async copyContentPageToOtherCustomer(pageId: string, toCustomerCode: string): Promise<void> {
        await this.apiService.copyContentPage(pageId, undefined, toCustomerCode);
    }

    public async copyNamedPageToOtherCustomer(pageTypeCode: string, toCustomerCode: string, ignoreAppErrorCodes: string[] = []): Promise<void> {
        const ignoreAppErrorCodesHeader: string | undefined = ignoreAppErrorCodes.length > 0 ? ignoreAppErrorCodes.join(',') : undefined;

        await this.apiService.copyNamedPage(CustomerContext.getCustomerCode(), pageTypeCode, toCustomerCode, undefined, ignoreAppErrorCodesHeader);
    }

    public async deleteContentPage(pageId: string): Promise<void> {
        await this.apiService.deleteContentPage(pageId);
    }

    public async createContentPage(page: ContentPageCreateRestDto): Promise<string> {
        const response: AxiosResponse<IdResponseRestDto> = await this.apiService.createContentPage(page);
        return response.data.id;
    }

    public async moveContent(pageId: string, moveAction: MoveActionEnum): Promise<void> {
        switch (moveAction) {
            case MoveActionEnum.TOP:
                await this.apiService.moveContentPageToTop(pageId);
                break;
            case MoveActionEnum.BOTTOM:
                await this.apiService.moveContentPageToBottom(pageId);
                break;
            case MoveActionEnum.UP:
                await this.apiService.moveContentPageUp(pageId);
                break;
            case MoveActionEnum.DOWN:
                await this.apiService.moveContentPageDown(pageId);
                break;
            default:
                throw new Error('Unsupported moveAction to call ' + moveAction);
        }
    }

    public async findContentPages() : Promise<PagedContentPageOverviewRestDto> {
        const response = await this.apiService.findContentPages();

        return response.data;
    }

    public async findContentPageTypes(isLibraryType: boolean): Promise<CodeDescriptionRestDto[]> {
        const response = await this.enumApiService.findPageTypeEnums(null, isLibraryType);

        return response.data;
    }

    public async findPageAbstractLibraries(filterIsOwner?: boolean): Promise<CodeDescriptionRestDto[]> {
        const response = await this.enumApiService.findPageAbstractLibraries(null, filterIsOwner);

        return response.data;
    }

    public async findPriceCards() : Promise<PagedContentPageOverviewRestDto> {
        const response = await this.apiService.findContentPages(null, null, 'PAGE_PRICE_CARD');

        return response.data;
    }

    public async getContentPage(id: string): Promise<TargetPageInfoRestDto> {
        const response = await this.abstractApiService.getContentPageOverviewCb(id);

        return response.data;
    }

    public async getContentPageWysiwyg(pageId: string): Promise<WysiwygContentPageRestDto> {
        const response = await this.apiService.getContentPageWysiwyg(pageId);

        return response.data;
    }

    /**
     * Returns null when the customer has no homepage yet; the backend answers 204 in that case.
     */
    public async getHomePageWysiwyg(): Promise<WysiwygContentPageRestDto | null> {
        const response = await this.apiService.getHomePageWysiwyg();

        return response.status === 204 ? null : response.data;
    }

    /**
     * Returns null when the customer has no page of this type yet; the backend answers 204 in that case.
     */
    public async getNamedPageWysiwyg(pageTypeCode: string): Promise<WysiwygContentPageRestDto | null> {
        const response = await this.apiService.getNamedPageWysiwyg(pageTypeCode);

        return response.status === 204 ? null : response.data;
    }

    public async updateContentPage(page: ContentPageRestDto, pageId: string): Promise<void> {
        await this.apiService.updateContentPage(page, pageId);
    }

    public async createHomePage(): Promise<void> {
        await this.apiService.createHomePage();
    }

    public async createNamedPage(pageTypeCode: string): Promise<void> {
        await this.apiService.createNamedPage(pageTypeCode);
    }

    public async pasteRowOnContentPage(rowId: string, toPageId: string): Promise<void> {
        await this.apiService.pasteRowOnContentPage({id: rowId} as IdRestDto, toPageId);
    }

    public async updateContentPageOverviewCbContent(content: string, pageId: string): Promise<void> {
        await this.abstractApiService.updateContentPageOverviewCbContent({content} as TextWithImageAbstractRestDto, pageId);
    }

    public async updateContentPageOverviewCbImage(imageKey: string, pageId: string): Promise<void> {
        await this.abstractApiService.updateContentPageOverviewCbImage({image_key: imageKey} as TextWithImageAbstractRestDto, pageId);
    }

    public async updateContentPageOverviewCbTitle(title: string, pageId: string): Promise<void> {
        await this.abstractApiService.updateContentPageOverviewCbTitle({title} as TextWithImageAbstractRestDto, pageId);
    }
}
